/**
 * The bar under the table — list engine v2's pagination row.
 *
 * "1–50 of 1,884 · Rows · 50 · Select all 1,884 for export" on the left, page
 * numbers and a Go to box on the right.
 *
 * The one idea worth naming: "Select all N for export" is a different thing
 * from ticking the rows on this page. Ticking is about the fifty rows in front
 * of you; this is about the whole filtered set, most of which you have never
 * seen. Export and bulk actions behave differently depending on which one is
 * on, so it says which, in words.
 */
import { useState } from 'react'
import { Button } from './primitives.js'
import { MenuSelect } from './menu-select.js'
import { IconChevronLeft, IconChevronRight } from '../frame/icons.js'
import { formatNumber } from '../lib/format.js'
import { cn } from '../lib/cn.js'

const PAGE_SIZES = [25, 50, 100, 200]

/**
 * Everything in this bar is 32px tall and nothing is 33.
 *
 * The arrows were buttons, the page number was a `min-w-8` pill, the Rows
 * picker and the Go-to box were `h-7` inputs, and the row read as four
 * different sizes of the same idea. A page number is a square the size of an
 * arrow: that is the whole rule.
 */
const CONTROL = 32

export interface ListPaginationProps {
  page: number
  pageCount: number
  pageSize: number
  /** Rows matching the filter, which is not the same as rows on this page. */
  total: number
  shown: number
  onPage: (page: number) => void
  onPageSize: (size: number) => void
  /** Ticking the whole filtered set, when the screen offers it. */
  selectAll?: { selected: boolean; onToggle: () => void }
}

/**
 * Which page buttons to draw: the first, the last, and a window around where
 * you are, with an ellipsis for whatever that skips.
 *
 * Returned as numbers and nulls rather than rendered here, so the gaps are
 * data and two adjacent gaps cannot happen.
 */
export function pageWindow(page: number, count: number): (number | null)[] {
  if (count <= 7) return Array.from({ length: count }, (_, index) => index)

  const near = [page - 1, page, page + 1].filter((n) => n > 0 && n < count - 1)
  const shown = new Set([0, ...near, count - 1])

  const out: (number | null)[] = []
  let previous = -1
  for (const index of [...shown].sort((a, b) => a - b)) {
    if (previous >= 0 && index > previous + 1) out.push(null)
    out.push(index)
    previous = index
  }
  return out
}

export function ListPagination({
  page, pageCount, pageSize, total, shown, onPage, onPageSize, selectAll,
}: ListPaginationProps) {
  const [goTo, setGoTo] = useState('')

  const first = total === 0 ? 0 : page * pageSize + 1
  const last = page * pageSize + shown

  const jump = () => {
    const wanted = Number(goTo)
    if (!Number.isFinite(wanted)) return
    // People type page numbers, not indexes.
    onPage(Math.min(Math.max(1, Math.round(wanted)), pageCount) - 1)
    setGoTo('')
  }

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line px-4 py-2 text-cell">
      <span className="tnum text-text-muted">
        {formatNumber(first)}–{formatNumber(last)} of {formatNumber(total)}
      </span>

      <span className="flex items-center gap-1.5 text-text-muted">
        <span>Rows</span>
        <MenuSelect
          label="Rows per page"
          value={String(pageSize)}
          onChange={(value) => { if (value) onPageSize(Number(value)) }}
          options={PAGE_SIZES.map((size) => ({ value: String(size), label: String(size) }))}
          minWidth={96}
          className="w-[72px]"
        />
      </span>

      {selectAll && total > shown && (
        <button
          type="button"
          onClick={selectAll.onToggle}
          className={cn('font-semibold text-accent hover:text-accent-dark',
            selectAll.selected && 'text-accent-dark')}
        >
          {selectAll.selected
            ? `All ${formatNumber(total)} selected · clear`
            : `Select all ${formatNumber(total)} for export`}
        </button>
      )}

      <div className="ml-auto flex items-center gap-1">
        <Button
          aria-label="Previous page"
          disabled={page === 0}
          onClick={() => onPage(page - 1)}
          style={{ width: CONTROL }}
          className="px-0"
        >
          <IconChevronLeft size={15} />
        </Button>

        {pageWindow(page, pageCount).map((index, position) => (
          index === null
            ? <span key={`gap-${position}`} className="px-1 text-text-faint">…</span>
            : (
              <button
                key={index}
                type="button"
                aria-current={index === page ? 'page' : undefined}
                onClick={() => onPage(index)}
                style={{ height: CONTROL, minWidth: CONTROL }}
                className={cn(
                  'tnum rounded-field px-2 text-cell',
                  index === page
                    ? 'bg-text font-semibold text-surface'
                    : 'text-text hover:bg-line-soft',
                )}
              >
                {index + 1}
              </button>
            )
        ))}

        <Button
          aria-label="Next page"
          disabled={page >= pageCount - 1}
          onClick={() => onPage(page + 1)}
          style={{ width: CONTROL }}
          className="px-0"
        >
          <IconChevronRight size={15} />
        </Button>

        <label className="ml-2 flex items-center gap-1.5 text-text-muted">
          <span>Go to</span>
          <input
            value={goTo}
            inputMode="numeric"
            onChange={(event) => setGoTo(event.target.value.replace(/[^\d]/g, ''))}
            onKeyDown={(event) => { if (event.key === 'Enter') jump() }}
            onBlur={jump}
            style={{ height: CONTROL }}
            className="tnum w-14 rounded-field border border-line bg-field px-1.5 text-cell text-text"
            aria-label={`Go to a page, 1 to ${pageCount}`}
          />
        </label>
      </div>
    </div>
  )
}
